import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import {
  EuiPanel,
  EuiTitle,
  EuiSpacer,
  EuiText,
  EuiBadge,
  EuiButton,
  EuiButtonGroup,
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiMarkdownEditor,
  EuiIcon,
  EuiHorizontalRule,
  EuiFlexGroup,
  EuiFlexItem,
  EuiCallOut,
  EuiCode,
  EuiLink,
  EuiLoadingSpinner,
  EuiEmptyPrompt,
  EuiToolTip,
  EuiHealth,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useToast } from "src/notifications";
import { useAnketaI18n } from "src/internationalization";
import { SentryFeedback, SentryInstanceFeedback } from "src/questionnaires";

type Severity = Sentry.SeverityLevel;

type TranslateFn = ReturnType<typeof useAnketaI18n>["translate"];

function relativeTime(dateString: string, translate: TranslateFn): string {
  const d = new Date(dateString);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (!Number.isFinite(diff)) return dateString ?? "-";
  if (diff < 60)
    return translate("anketa.opsanio.appliance.flyout.time.justNow", {
      defaultMessage: "Just now",
    });
  if (diff < 3600)
    return translate("anketa.opsanio.appliance.flyout.time.minutesAgo", {
      values: { count: Math.floor(diff / 60) },
      defaultMessage: "{{count}} min ago",
    });
  if (diff < 86400)
    return translate("anketa.opsanio.appliance.flyout.time.hoursAgo", {
      values: { count: Math.floor(diff / 3600) },
      defaultMessage: "{{count}} h ago",
    });
  if (diff < 2592000)
    return translate("anketa.opsanio.appliance.flyout.time.daysAgo", {
      values: { count: Math.floor(diff / 86400) },
      defaultMessage: "{{count}} days ago",
    });
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleString("de-DE", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const STATUS_COLOR: Record<string, string> = {
  unresolved: "warning",
  resolved: "success",
  ignored: "subdued",
};

const LEVEL_COLOR: Record<string, string> = {
  fatal: "danger",
  error: "danger",
  warning: "warning",
  info: "primary",
  debug: "subdued",
};

const FeedbackCard = ({
  item,
  translate,
}: {
  item: SentryFeedback;
  translate: TranslateFn;
}) => (
  <EuiPanel
    paddingSize="m"
    hasShadow={false}
    hasBorder
    style={{ marginBottom: 8 }}
  >
    <EuiFlexGroup
      justifyContent="spaceBetween"
      alignItems="center"
      gutterSize="s"
      responsive={false}
    >
      <EuiFlexItem grow={false}>
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiHealth color={STATUS_COLOR[item.status] ?? "subdued"}>
              {item.status}
            </EuiHealth>
          </EuiFlexItem>
          {item.level && (
            <EuiFlexItem grow={false}>
              <EuiBadge color={LEVEL_COLOR[item.level] ?? "hollow"}>
                {item.level}
              </EuiBadge>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiToolTip content={formatDate(item.lastSeen)}>
          <EuiText size="xs" color="subdued">
            {relativeTime(item.lastSeen, translate)}
          </EuiText>
        </EuiToolTip>
      </EuiFlexItem>
    </EuiFlexGroup>
    <EuiSpacer size="s" />
    <EuiText size="s" style={{ whiteSpace: "pre-wrap" }}>
      {item.message}
    </EuiText>
    <EuiSpacer size="s" />
    <EuiFlexGroup
      gutterSize="s"
      alignItems="center"
      responsive={false}
      justifyContent="spaceBetween"
    >
      <EuiFlexItem grow={false}>
        <EuiText size="xs" color="subdued">
          {item.shortId}
          {item.count > 1 ? ` · ${item.count}×` : ""}
        </EuiText>
      </EuiFlexItem>
      {item.permalink && (
        <EuiFlexItem grow={false}>
          <EuiLink href={item.permalink} target="_blank" external>
            {translate("anketa.opsanio.appliance.flyout.feedback.openInSentry", {
              defaultMessage: "Open in Sentry",
            })}
          </EuiLink>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  </EuiPanel>
);

export const OpsanioApplianceFlyoutFeedback = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();
  const { user } = useAnketaAuth();

  const instanceName = appliance.common?.metadata?.name ?? "";

  // --- Read: existing Sentry user feedback for this instance ---------------
  const [feedback, setFeedback] = useState<SentryFeedback[]>([]);
  const [environments, setEnvironments] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<boolean>(false);

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string" && instanceName.trim() !== "",
    [keycloak, instanceName],
  );

  const getFeedback = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    setLoadError(null);
    setUnavailable(false);
    try {
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/sentry/feedback/instance/${encodeURIComponent(
          instanceName,
        )}`,
        responseType: "json",
        token: keycloak.token,
      });
      if (error) {
        if ((error as any).status === 503) {
          setUnavailable(true);
        } else {
          setLoadError(error.message);
        }
      } else {
        const payload = (data as any)?.data as
          | SentryInstanceFeedback
          | undefined;
        setFeedback(Array.isArray(payload?.feedback) ? payload!.feedback : []);
        setEnvironments(
          Array.isArray(payload?.environments) ? payload!.environments : [],
        );
      }
    } catch (err) {
      console.error(err);
      setLoadError(
        translate("anketa.opsanio.appliance.flyout.error.unexpected", {
          defaultMessage: "Unexpected error",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [canFetch, instanceName, translate]);

  useEffect(() => {
    getFeedback();
  }, [appliance]);

  // --- Write: submit new feedback (Sentry.captureFeedback), scoped to instance
  const reporterName = useMemo(
    () =>
      user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : (user?.username ?? ""),
    [user],
  );
  const reporterEmail = user?.email ?? "";

  const [severity, setSeverity] = useState<Severity>("info");
  const [message, setMessage] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const feedbackTypes = useMemo(
    () => [
      {
        id: "info",
        label: translate("anketa.feedback.form.type.general", {
          defaultMessage: "Allgemein",
        }),
      },
      {
        id: "error",
        label: translate("anketa.feedback.form.type.bug", {
          defaultMessage: "Fehler",
        }),
      },
      {
        id: "warning",
        label: translate("anketa.feedback.form.type.feature", {
          defaultMessage: "Verbesserung",
        }),
      },
    ],
    [translate],
  );

  const canSubmit =
    message.trim() !== "" && reporterEmail.trim() !== "" && !submitting;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const eventId = Sentry.captureMessage(
        `Opsanio Feedback [${severity.toUpperCase()}] ${instanceName}`,
        severity,
      );
      Sentry.withScope((scope) => {
        scope.setUser({ email: reporterEmail, username: reporterName });
        scope.setTag("feedback.type", severity);
        scope.setTag("opsanio.instance", instanceName);
        scope.setContext("opsanio_appliance", {
          instance: instanceName,
          applianceId: appliance.id,
          displayName: appliance.displayName,
          catalogId: appliance.catalogId,
        });
        Sentry.captureFeedback({
          associatedEventId: eventId,
          name: reporterName || "Unknown",
          email: reporterEmail,
          message: `[${instanceName}] ${message.trim()}`,
        });
      });

      setMessage("");
      addToast({
        title: translate("anketa.feedback.submit.success.title", {
          defaultMessage: "Feedback gesendet",
        }),
        color: "success",
        text: translate("anketa.feedback.submit.success.message", {
          defaultMessage: "Vielen Dank für Ihr Feedback!",
        }),
      });
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      Sentry.captureException(err, {
        tags: {
          section: "opsanio_appliance_feedback",
          action: "submit_failed",
        },
      });
      setSubmitError(
        translate("anketa.feedback.submit.error.message", {
          defaultMessage:
            "Feedback konnte nicht gesendet werden. Bitte erneut versuchen.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    canSubmit,
    severity,
    instanceName,
    reporterEmail,
    reporterName,
    message,
    appliance,
    addToast,
    translate,
  ]);

  return (
    <EuiPanel
      paddingSize="m"
      hasShadow={false}
      hasBorder={false}
      color="transparent"
    >
      <EuiFlexGroup
        justifyContent="spaceBetween"
        alignItems="center"
        gutterSize="s"
        responsive={false}
      >
        <EuiFlexItem>
          <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiIcon type="reporter" size="l" />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiTitle size="s">
                <h3 style={{ margin: 0 }}>Sentry Feedback</h3>
              </EuiTitle>
            </EuiFlexItem>
            {!loading && feedback.length > 0 && (
              <EuiFlexItem grow={false}>
                <EuiBadge color="hollow">{feedback.length}</EuiBadge>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            onClick={getFeedback}
            size="s"
            iconType="refresh"
            isLoading={loading}
            disabled={loading}
          >
            {translate("anketa.common.update")}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="xs" />
      <EuiText size="xs" color="subdued">
        <EuiIcon type="package" size="s" /> {instanceName}
        {environments.length > 0 && (
          <>
            {" · "}
            {translate(
              "anketa.opsanio.appliance.flyout.feedback.environments.count",
              {
                values: { count: environments.length },
                defaultMessage: "{{count}} environment(s)",
              },
            )}
          </>
        )}
      </EuiText>

      <EuiHorizontalRule margin="m" />

      {loading && (
        <EuiFlexGroup
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: 160 }}
        >
          <EuiFlexItem grow={false}>
            <EuiLoadingSpinner size="xl" />
          </EuiFlexItem>
        </EuiFlexGroup>
      )}

      {!loading && unavailable && (
        <EuiEmptyPrompt
          iconType="offline"
          title={
            <h3>
              {translate(
                "anketa.opsanio.appliance.flyout.feedback.unavailable.title",
                { defaultMessage: "Sentry unavailable" },
              )}
            </h3>
          }
          body={
            <EuiText size="s" color="subdued">
              {translate(
                "anketa.opsanio.appliance.flyout.feedback.unavailable.body",
                {
                  defaultMessage:
                    "The Sentry connection is currently not configured.",
                },
              )}
            </EuiText>
          }
        />
      )}

      {!loading && !unavailable && loadError && (
        <EuiCallOut
          title={translate("anketa.opsanio.error.loading")}
          color="danger"
          iconType="alert"
        >
          <p>{loadError}</p>
          <EuiButton size="s" color="danger" onClick={getFeedback}>
            {translate("anketa.common.retry")}
          </EuiButton>
        </EuiCallOut>
      )}

      {!loading && !unavailable && !loadError && feedback.length === 0 && (
        <EuiEmptyPrompt
          icon={<EuiIcon type="reporter" size="xl" color="subdued" />}
          title={
            <h3>
              {translate(
                "anketa.opsanio.appliance.flyout.feedback.empty.title",
                { defaultMessage: "No feedback yet" },
              )}
            </h3>
          }
          body={
            <EuiText size="s" color="subdued">
              {translate("anketa.opsanio.appliance.flyout.feedback.empty.body", {
                defaultMessage:
                  "No Sentry user feedback has been recorded for this instance yet.",
              })}
            </EuiText>
          }
        />
      )}

      {!loading && !unavailable && !loadError && feedback.length > 0 && (
        <>
          {feedback.map((item) => (
            <FeedbackCard key={item.id} item={item} translate={translate} />
          ))}
        </>
      )}
    </EuiPanel>
  );
};
